import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Space,
} from "antd";
import { ExpandOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  CHARGE_CATEGORIES,
  CHARGE_SCOPES,
  OLIVEEB_APP_OPTIONS,
  APP_STAGES,
  CURRENCIES,
  CHARGE_ITEM_CODES,
} from "./ChargesConfigPage";

const { RangePicker } = DatePicker;

const ChargeConfigDrawer = ({ open, onClose, charge, onSubmit }) => {
  const [form] = Form.useForm();
  const [isExpanded, setIsExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [valueType, setValueType] = useState("PERCENTAGE");

  useEffect(() => {
    if (charge) {
      form.setFieldsValue({
        ...charge,
        effectiveRange: [
          dayjs(charge.effectiveFrom),
          charge.effectiveTo ? dayjs(charge.effectiveTo) : null,
        ],
      });
      setValueType(charge.valueType);
    } else {
      form.resetFields();
      form.setFieldsValue({
        valueType: "PERCENTAGE",
        displayOrder: 1,
        currency: "USD",
        country: "US",
        state: null,
        county: null,
        city: null,
        status: "ACTIVE",
      });
      setValueType("PERCENTAGE");
    }
  }, [charge, form]);

  const handleFinish = async (values) => {
    const [from] = values.effectiveRange || []; // only take start date

    const payload = {
      code: values.code,
      name: values.name,
      description: values.description,
      category: values.category,
      valueType: values.valueType,
      value:
        values.valueType === "PERCENTAGE" ? values.value / 100 : values.value,
      scope: values.scope,
      applyOn: values.applyOn,
      displayOrder: values.displayOrder,
      status: values.status,
      app: values.app,
      stage: values.stage,
      currency: values.currency,
      country: values.country,
      effectiveFrom: from ? from.startOf("day").toISOString() : null,
      chargeTarget: values.chargeTarget,
      minAmount: values.minAmount,
      maxAmount: values.maxAmount,
    };


    try {
      setSubmitting(true);
      await onSubmit(payload);

      form.resetFields();
      onClose?.();

    } catch (error) {
      console.error("Charge creation failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  return (
    <Drawer
      size={isExpanded ? 640 : "100vw"}
      open={open}
      closable={false}
      onClose={handleClose}
      destroyOnHidden
      zIndex={9999}
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: isExpanded ? "100vw" : 900,
            margin: isExpanded ? 0 : "0 auto",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="primary" loading={submitting}
            disabled={submitting} onClick={() => form.submit()}>
            {charge ? "Save Changes" : "Create Charge"}
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 900,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, san-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 16, color: "#6b7280" }}>
            <span style={{ color: "#111827", fontWeight: 700 }}>
              {charge ? "Edit Charge" : "Create Charge"}
            </span>
          </div>
          <ExpandOutlined
            className="task-drawer-icon"
            onClick={() => setIsExpanded((prev) => !prev)}
            style={{ cursor: "pointer" }}
          />
        </div>

        <Form layout="vertical" form={form} onFinish={handleFinish}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Code" name="code" rules={[{ required: true }]}>
                <Select
                  showSearch={{
                    filterOption: (input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase()),
                  }}
                  options={CHARGE_ITEM_CODES.map((c) => ({
                    value: c,
                    label: c,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Category" name="category">
                <Select options={CHARGE_CATEGORIES} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Apply On" name="applyOn">
                <Select
                  options={[
                    { value: "FEE_TAX_BASE", label: "Fee + Tax Base" },
                    { value: "TOKEN_ONLY", label: "Token Only" },
                    { value: "TIP_ONLY", label: "Tip Only" },
                    { value: "CASH_PORTION", label: "Cash Portion" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Value Type" name="valueType">
                <Select
                  onChange={(v) => setValueType(v)}
                  options={[
                    { value: "PERCENTAGE", label: "Percentage" },
                    { value: "FIXED", label: "Fixed" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={valueType === "PERCENTAGE" ? "Percentage (%)" : "Amount"}
                name="value"
                rules={[{ required: true }]}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <InputNumber style={{ width: "100%" }} />
                  {valueType === "PERCENTAGE" && (
                    <span
                      className="span-addon"
                    >
                      %
                    </span>
                  )}
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Currency" name="currency">
                <Select options={CURRENCIES} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Display Order" name="displayOrder">
                <InputNumber min={1} max={10} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Charge Target" name="chargeTarget" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "CHECKOUT", label: "CHECKOUT" },
                { value: "PAYOUT", label: "PAYOUT" },
              ]}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Min Amount" name="minAmount">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Max Amount" name="maxAmount">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <h4 className="text-sm">Location Scope</h4>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Scope" name="scope">
                <Select
                  options={CHARGE_SCOPES.map((s) => ({ value: s, label: s }))}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Status" name="status">
                <Select
                  options={[
                    { value: "ACTIVE", label: "Active" },
                    { value: "INACTIVE", label: "Inactive" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Country" name="country">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="State" name="state">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="County" name="county">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="City" name="city">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Oliveeb Product" name="app">
                <Select
                  options={OLIVEEB_APP_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Stage" name="stage">
                <Select
                  options={APP_STAGES.map((s) => ({ value: s, label: s }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <h4 className="text-sm">Effective Period</h4>
          <Form.Item name="effectiveRange" rules={[{ required: true }]}>
            <RangePicker showTime style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
};

export default ChargeConfigDrawer;
